import { User } from "@/modules/user/class-definitions/user";
import { QUERY_USER_BY_AUTH_ID } from "@/modules/user/graphql/FetchUserByAuthId";
import { firebase } from "@/utilities/firebase";
import { apolloClientFactory, headersBuilder } from "@/utilities/ApolloClient";

/**
 * @description - Class designed to manage authentication.
 */
export class AuthenticationManager {
  constructor() {
    /**
     * - A firebase auth object
     * @type {object}
     */
    this.authObject = {};
    /**
     * @type {User}
     */
    this.userInfo = new User();
  }

  checkAuthentication(authUser) {
    return new Promise((resolve, reject) => {
      this.authObject = authUser;
      this.userInfo.authId = authUser.uid;
      this.userInfo.name = authUser.displayName;
      this.userInfo.email = authUser.email;
      this.userInfo.photoURL = authUser.photoURL;

      // Get a new ID token from Firebase.
      this.getIdToken()
        .then(() => {
          // Check for Hasura custom claims
          this.getHasuraClaim()
            .then(hasuraClaim => {
              // If the hasura custom claims exist, construct a header object and a new apollo client factory.
              if (hasuraClaim) {
                const scopedApolloHeaders = headersBuilder(
                  this.userInfo.idToken
                );

                const scopedApolloClient = apolloClientFactory(
                  scopedApolloHeaders
                );

                // Collect the users information from the DB and return it to the consuming functionality.
                this.retrieveUserFromDatabase(scopedApolloClient)
                  .then(() => {
                    resolve(this.userInfo);
                  })
                  .catch(error => {
                    console.error("error retrieving user from DB: ", error);
                    reject(error);
                  });
              } else {
                // Make sure the authentication custom claims have been regenerated recently
                this.checkClaimsHaveBeenGenerated()
                  .then(() => {
                    // Construct a header object and a new apollo client factory.
                    const scopedApolloHeaders = headersBuilder(
                      this.userInfo.idToken
                    );
                    const scopedApolloClient = apolloClientFactory(
                      scopedApolloHeaders
                    );

                    // Collect the users information from the DB and return it to the consuming functionality.
                    this.retrieveUserFromDatabase(scopedApolloClient)
                      .then(() => {
                        resolve(this.userInfo);
                      })
                      .catch(error => {
                        console.error("error retrieving user from DB: ", error);
                        reject(error);
                      });
                  })
                  .catch(error => {
                    console.error("error refreshing hasura claims: ", error);
                  });
              }
            })
            .catch(error => {
              console.error("error retrieving hasura claims: ", error);
              reject(error);
            });
        })
        .catch(error => {
          console.error("error retrieving idToken: ", error);
        });
    });
  }

  /**
   * - Generates a new Auth token and forces a refresh of the token validation.
   * - Sets the classes userInfo variables to values from the token.
   * @returns {Promise}
   */
  getIdToken() {
    return new Promise((resolve, reject) => {
      this.authObject
        .getIdToken(true)
        .then(idToken => {
          this.userInfo.idToken = idToken;
          this.userInfo.idTokenLoaded = true;
          resolve();
        })
        .catch(error => {
          console.error("error getting idToken: ", error);
          reject(error);
        });
    });
  }

  /**
   * - Checks the last time that custom claims were generated for the current user.
   * - If this does not exist, throws an error.
   * - Else, pulls in fresh claims and updatezs the class instance.
   * @returns {Promise}
   */
  checkClaimsHaveBeenGenerated() {
    return new Promise((resolve, reject) => {
      // Defines a reference to the firebase RTD location where the refresh time records are stored.
      const metadataReference = firebase
        .database()
        .ref("metadata/" + this.userInfo.authId + "/refreshTime");

      // Set a listener on the refresh time vale
      metadataReference.on("value", data => {
        // if there is nothing in the DB, throw an error
        if (!data.exists) {
          reject("no data exists for user");
        }

        // Force refresh to pick up the latest custom claims changes.
        this.refreshAuthenticationClaims()
          .then(() => {
            resolve();
          })
          .catch(error => {
            console.error("error refreshing authentication claims: ", error);
            reject(error);
          });
      });
    });
  }

  /**
   * - Call firebase and generate a new ID token, with a forced refresh.
   * - Then set the class instances userInfo data based on the refreshed ID token.
   * @returns {Promise}
   */
  refreshAuthenticationClaims() {
    return new Promise((resolve, reject) => {
      this.authObject
        .getIdTokenResult(true)
        .then(result => {
          this.userInfo.idToken = result.token;
          this.userInfo.idTokenExpirationTime = result.expirationTime;
          resolve();
        })
        .catch(error => {
          console.error("error refreshing token: ", error);
          reject(error);
        });
    });
  }

  /**
   * - Extracts the decoded content of the ID token.
   * - Checks if there are hasura custom claims in the decoded content and returns them.
   * @returns {Promise<object>}
   */
  getHasuraClaim() {
    return new Promise((resolve, reject) => {
      this.authObject
        .getIdTokenResult(true)
        .then(idTokenResult => {
          let hasuraClaim =
            idTokenResult.claims["https://hasura.io/jwt/claims"];
          this.userInfo.idToken = idTokenResult.token;
          this.userInfo.idTokenExpirationTime = idTokenResult.expirationTime;
          resolve(hasuraClaim);
        })
        .catch(error => {
          console.error("error getting ID token result from firebase: ", error);
          reject(error);
        });
    });
  }

  /**
   * - takes in an Apollo Client object that is scoped to this code block.
   * - Retrieves the user record from the DB.
   * - If no record exists matching the firebase users ID, create a new firebase user.
   * @param {ApolloClient} scopedApolloClient
   * @returns {Promise}
   */
  retrieveUserFromDatabase(scopedApolloClient) {
    return new Promise((resolve, reject) => {
      // Call the DB and retrieve the user.
      scopedApolloClient
        .query({
          query: QUERY_USER_BY_AUTH_ID,
          variables: {
            authId: this.userInfo.authId
          }
        })
        .then(result => {
          let returnedUser = result.data.user[0];

          // If there is no user available, create a new one
          if (!returnedUser) {
            this.createNewTeamAndUser()
              .then(() => {
                resolve();
              })
              .catch(error => {
                console.error("error creating new team and user: ", error);
                reject(error);
              });
            // other wise, set this class instances userInfo details based on the user that was returned from the DB.
          } else if (returnedUser) {
            this.userInfo.id = returnedUser.id;
            this.userInfo.team.id = returnedUser.team_id;
            this.userInfo.hospitalId = returnedUser.hospital_id;

            resolve();
          }
        })
        .catch(error => {
          console.error("error queriying user by auth id: ", error);
          reject(error);
        });
    });
  }

  /**
   * - saves this class instances userInfo to the DB.
   * - When the response is returned, collect the team ID from that and attach it to this users info
   * @returns {Promise}
   */
  createNewTeamAndUser() {
    return new Promise((resolve, reject) => {
      this.userInfo
        .saveUser()
        .then(response => {
          let team = response.data.insert_team.returning[0];
          let newUser = team.users_by_teamId[0];
          this.userInfo.id = newUser.id;
          this.userInfo.team = team;
          this.userInfo.hospitalId = newUser.hospital_id;
          this.userInfo.newlyCreatedUser = true;

          resolve();
        })
        .catch(error => {
          console.error("error saving the user: ", error);
          reject(error);
        });
    });
  }
}
